import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';

// Define the expected state type:
interface LocationState {
  from?: { pathname: string };
}

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

export default function SignUpPage(): React.ReactElement {
  const { signup } = useAuth();
  const [formData, setFormData] = useState<SignUpFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const [step, setStep] = useState<'form' | 'success'>('form');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const location = useLocation();
  const from = (location.state as LocationState)?.from?.pathname || null;

  const handleSubmit = async () => {
    setFormError('');

    if (!formData.acceptTerms) {
      setFormError("Vous devez accepter les conditions d'utilisation");
      return;
    }

    if (!/\d/.test(formData.password) || !/[A-Z]/.test(formData.password) || formData.password.length < 8) {
      setFormError("Le mot de passe doit contenir au moins 8 caractères, une majuscule et un chiffre");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setFormError('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      setLoading(true);

      // Call the signup API
      const result = await authAPI.signup({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password
      });

      console.log('Signup response:', result.data);

      // Send verification email
      await authAPI.sendVerificationEmail(formData.email);
      setStep('success');

    } catch (err: any) {
      console.error('Signup error:', err);

      if (err.response && err.response.data) {
        // Handle both 'error' and 'message' fields from backend
        const errorMessage = err.response.data.error || err.response.data.message || "Erreur lors de l'inscription.";
        setFormError(errorMessage);
      } else {
        setFormError("Erreur lors de l'inscription.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = (): void => {
    if (from) {
      sessionStorage.setItem('authRedirect', from);
    }
    window.location.href = authAPI.getGoogleAuthUrl();
  };

  const handleFacebookAuth = (): void => {
    if (from) {
      sessionStorage.setItem('authRedirect', from);
    }
    window.location.href = authAPI.getFacebookAuthUrl();
  };

  const handleLinkedInAuth = (): void => {
    if (from) {
      sessionStorage.setItem('authRedirect', from);
    }
    window.location.href = authAPI.getLinkedInAuthUrl();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-accent py-12 px-4 sm:px-6 lg:px-8 dark:bg-gray-900">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="mt-6 text-3xl font-bold font-serif">Créer un compte</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Rejoignez notre communauté pour accéder à toutes nos ressources
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            {step === 'form' && (
              <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first-name">Prénom</Label>
                    <Input id="first-name" name="firstName" value={formData.firstName} onChange={handleChange} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last-name">Nom</Label>
                    <Input id="last-name" name="lastName" value={formData.lastName} onChange={handleChange} required />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input id="password" name="password" type="password" value={formData.password} onChange={handleChange} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                  <Input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} required />
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    name="acceptTerms"
                    className="mt-1"
                    checked={formData.acceptTerms}
                    onCheckedChange={(checked: boolean | string) =>
                      setFormData(prev => ({ ...prev, acceptTerms: Boolean(checked) }))
                    }
                  />
                  <Label htmlFor="terms" className="text-sm">
                    J'accepte les{" "}
                    <a href="/terms" className="text-elille-blue hover:underline">conditions d'utilisation</a> et{" "}
                    <a href="/privacy" className="text-elille-blue hover:underline">la politique de confidentialité</a>
                  </Label>
                </div>

                <Button type="button" onClick={handleSubmit} className="w-full" disabled={loading}>
                  {loading ? 'Création en cours...' : 'Créer mon compte'}
                </Button>

                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                    {formError}
                  </div>
                )}
              </form>
            )}

            {step === 'success' && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-medium">Compte créé avec succès !</h3>
                <p className="text-sm text-gray-600">
                  Un email de vérification a été envoyé à <strong>{formData.email}</strong>.
                  Veuillez cliquer sur le lien dans l'email pour activer votre compte.
                </p>
                <p className="text-xs text-gray-500">
                  Vous ne voyez pas l'email ? Vérifiez votre dossier spam.
                </p>
              </div>
            )}
          </CardContent>

          {step === 'form' && (
            <CardFooter className="flex flex-col space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Ou continuer avec</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-3">
                {/* Google Auth Button */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleAuth}
                  type="button"
                  disabled={loading}
                >
                  <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
                    <path d="M 26 2 C 13.308594 2 3 12.308594 3 25 C 3 37.691406 13.308594 48 26 48 C 35.917969 48 41.972656 43.4375 45.125 37.78125 C 48.277344 32.125 48.675781 25.480469 47.71875 20.9375 L 47.53125 20.15625 L 46.75 20.15625 L 26 20.125 L 25 20.125 L 25 30.53125 L 36.4375 30.53125 C 34.710938 34.53125 31.195313 37.28125 26 37.28125 C 19.210938 37.28125 13.71875 31.789063 13.71875 25 C 13.71875 18.210938 19.210938 12.71875 26 12.71875 C 29.050781 12.71875 31.820313 13.847656 33.96875 15.6875 L 34.6875 16.28125 L 41.53125 9.4375 L 42.25 8.6875 L 41.5 8 C 37.414063 4.277344 31.960938 2 26 2 Z M 26 4 C 31.074219 4 35.652344 5.855469 39.28125 8.84375 L 34.46875 13.65625 C 32.089844 11.878906 29.199219 10.71875 26 10.71875 C 18.128906 10.71875 11.71875 17.128906 11.71875 25 C 11.71875 32.871094 18.128906 39.28125 26 39.28125 C 32.550781 39.28125 37.261719 35.265625 38.9375 29.8125 L 39.34375 28.53125 L 27 28.53125 L 27 22.125 L 45.84375 22.15625 C 46.507813 26.191406 46.066406 31.984375 43.375 36.8125 C 40.515625 41.9375 35.320313 46 26 46 C 14.386719 46 5 36.609375 5 25 C 5 13.390625 14.386719 4 26 4 Z"></path>
                  </svg>
                  Google
                </Button>

                {/* Facebook Auth Button */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleFacebookAuth}
                  type="button"
                  disabled={loading}
                >
                  <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </Button>

                {/* LinkedIn Auth Button */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleLinkedInAuth}
                  type="button"
                  disabled={loading}
                >
                  <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn
                </Button>
              </div>

              <p className="mt-4 text-center text-sm text-gray-500">
                Vous avez déjà un compte ?{' '}
                <a href="/signin" className="font-medium text-elille-blue hover:underline">Se connecter</a>
              </p>
            </CardFooter>
          )}

          {step === 'success' && (
            <CardFooter className="flex flex-col space-y-4">
              <p className="text-center text-sm text-gray-500">
                <a href="/signin" className="font-medium text-elille-blue hover:underline">Retour à la connexion</a>
              </p>
            </CardFooter>
          )}
        </Card>
      </div>
    </div>
  );
}
